// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Plus, Users, Palette, DollarSign, Edit, Save, X, User, Mail, Calendar, TrendingUp, Package } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface GraphicsVendor {
  id: string;
  user_id: string;
  email: string;
  name: string;
  business_name?: string;
  commission_rate?: number;
  created_at: string;
  verification_status: string;
  is_active: boolean;
  total_orders: number;
  total_revenue: number;
}

interface VendorReview {
  id: string;
  reviewer_name: string;
  rating: number;
  review_text?: string;
  created_at: string;
}

const GraphicsVendorsTab: React.FC = () => {
  const [vendors, setVendors] = useState<GraphicsVendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingVendor, setEditingVendor] = useState<string | null>(null);
  const [editCommissionRate, setEditCommissionRate] = useState('');
  const [editBusinessName, setEditBusinessName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCreateVendor, setShowCreateVendor] = useState(false);
  const [vendorReviews, setVendorReviews] = useState<VendorReview[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  useEffect(() => {
    fetchGraphicsVendors();
  }, []);

  const fetchGraphicsVendors = async () => {
    try {
      setIsLoading(true);
      
      // Fetch graphics vendor profiles
      const { data: vendorProfiles, error: vendorError } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .select(`
          id,
          user_id,
          business_name,
          commission_rate,
          verification_status,
          is_active,
          created_at
        `);

      if (vendorError) throw vendorError;

      // Fetch profile data separately
      const userIds = vendorProfiles?.map(v => v.user_id).filter(Boolean) || [];
      const { data: profileData } = await supabase
        .from('profiles')
        .select('user_id, id, email, name')
        .in('user_id', userIds);

      // Create profile map
      const profileMap = new Map(profileData?.map(p => [p.user_id, p]) || []);

      // Get order statistics for each vendor
      const vendorsWithStats = await Promise.all(
        vendorProfiles?.map(async (vendorProfile) => {
          const profile = profileMap.get(vendorProfile.user_id);
          // Count total orders for this vendor
          const { count: orderCount } = await supabase
            .from('graphics_orders')
            .select('*', { count: 'exact', head: true })
            .eq('vendor_id', vendorProfile.id);

          // Calculate total revenue for this vendor
          const { data: orders } = await (supabase as any)
            .from('graphics_orders')
            .select('total_amount')
            .eq('vendor_id', vendorProfile.id);

          const totalRevenue = orders?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;

          return {
            id: vendorProfile.id,
            user_id: vendorProfile.user_id,
            email: profile?.email || 'N/A',
            name: profile?.name || 'N/A',
            business_name: vendorProfile.business_name,
            commission_rate: vendorProfile.commission_rate,
            created_at: vendorProfile.created_at,
            verification_status: vendorProfile.verification_status,
            is_active: vendorProfile.is_active,
            total_orders: orderCount || 0,
            total_revenue: totalRevenue
          };
        }) || []
      );

      setVendors(vendorsWithStats);
    } catch (error: any) {
      console.error('Error fetching graphics vendors:', error);
      toast.error(error.message || 'Failed to load graphics vendors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (vendorId: string, isActive: boolean) => {
    try {
      const { error } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .update({ is_active: isActive })
        .eq('id', vendorId);

      if (error) throw error;

      setVendors(prevVendors =>
        prevVendors.map(vendor =>
          vendor.id === vendorId ? { ...vendor, is_active: isActive } : vendor
        )
      );

      toast.success(`Vendor ${isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      console.error('Error toggling vendor active status:', error);
      toast.error(error.message || 'Failed to update vendor status');
    }
  };

  const fetchVendorReviews = async (vendorId: string) => {
    try {
      setLoadingReviews(true);
      const { data: reviews, error } = await supabase
        .from('graphics_vendor_reviews')
        .select(`
          id,
          rating,
          review_text,
          created_at,
          reviewer_id,
          profiles!graphics_vendor_reviews_reviewer_id_fkey (
            name
          )
        `)
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match the expected interface
      const transformedReviews = reviews?.map(review => ({
        id: review.id,
        reviewer_name: (review.profiles as any)?.name || 'Anonymous',
        rating: review.rating,
        review_text: review.review_text,
        created_at: review.created_at
      })) || [];

      setVendorReviews(transformedReviews);
    } catch (error: any) {
      console.error('Error fetching vendor reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleEditVendor = (vendor: GraphicsVendor) => {
    setEditingVendor(vendor.id);
    setEditCommissionRate(vendor.commission_rate?.toString() || '');
    setEditBusinessName(vendor.business_name || '');
    fetchVendorReviews(vendor.id);
  };

  const handleCancelEdit = () => {
    setEditingVendor(null);
    setEditCommissionRate('');
    setEditBusinessName('');
    setVendorReviews([]);
  };

  const handleUpdateVendor = async (vendorId: string) => {
    try {
      setIsUpdating(true);
      
      const rate = parseFloat(editCommissionRate);
      if (editCommissionRate && (isNaN(rate) || rate < 0 || rate > 100)) {
        toast.error('Commission rate must be between 0 and 100');
        return;
      }

      if (!editBusinessName.trim()) {
        toast.error('Business name is required');
        return;
      }

      const updateData: any = {
        business_name: editBusinessName.trim()
      };

      if (editCommissionRate) {
        updateData.commission_rate = rate;
      }

      const { error } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .update(updateData)
        .eq('id', vendorId);

      if (error) throw error;

      // Update local vendor data
      setVendors(prevVendors =>
        prevVendors.map(vendor =>
          vendor.id === vendorId 
            ? { 
                ...vendor, 
                business_name: editBusinessName.trim(),
                commission_rate: editCommissionRate ? rate : vendor.commission_rate
              } 
            : vendor
        )
      );
      
      setEditingVendor(null);
      setEditCommissionRate('');
      setEditBusinessName('');
      toast.success('Vendor updated successfully');
    } catch (error: any) {
      console.error('Error updating vendor:', error);
      toast.error(error.message || 'Failed to update vendor');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateVerificationStatus = async (vendorId: string, newStatus: string) => {
    try {
      const { error } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .update({ verification_status: newStatus })
        .eq('id', vendorId);

      if (error) throw error;

      // Update local vendor data
      setVendors(prevVendors =>
        prevVendors.map(vendor =>
          vendor.id === vendorId ? { ...vendor, verification_status: newStatus } : vendor
        )
      );

      toast.success(`Vendor ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully`);
    } catch (error: any) {
      console.error('Error updating verification status:', error);
      toast.error(error.message || 'Failed to update verification status');
    }
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading graphics vendors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Graphics Vendors</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vendors.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <Palette className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vendors.reduce((sum, vendor) => sum + vendor.total_orders, 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${vendors.reduce((sum, vendor) => sum + vendor.total_revenue, 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Graphics Vendors ({filteredVendors.length})</CardTitle>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Button onClick={() => setShowCreateVendor(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Vendor Account
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredVendors.length === 0 ? (
            <div className="text-center py-8">
              <Palette className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'No vendors found matching your search.' : 'No graphics vendors found.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Business Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Verification</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((vendor) => (
                  <React.Fragment key={vendor.id}>
                    <TableRow>
                      <TableCell className="font-medium">
                        {vendor.name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {editingVendor === vendor.id ? (
                          <Input
                            type="text"
                            value={editBusinessName}
                            onChange={(e) => setEditBusinessName(e.target.value)}
                            className="w-40"
                            placeholder="Business Name"
                          />
                        ) : (
                          <span>{vendor.business_name || 'N/A'}</span>
                        )}
                      </TableCell>
                      <TableCell>{vendor.email}</TableCell>
                      <TableCell>
                        {editingVendor === vendor.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={editCommissionRate}
                              onChange={(e) => setEditCommissionRate(e.target.value)}
                              className="w-20"
                              placeholder="Rate"
                            />
                            <span className="text-sm">%</span>
                          </div>
                        ) : (
                          <span>{vendor.commission_rate ? `${vendor.commission_rate}%` : 'Not set'}</span>
                        )}
                      </TableCell>
                      <TableCell>{vendor.total_orders}</TableCell>
                      <TableCell>${vendor.total_revenue.toFixed(2)}</TableCell>
                      <TableCell>
                        {vendor.verification_status === 'pending' ? (
                          <Badge 
                            variant="secondary"
                            className="cursor-pointer hover:bg-green-100 hover:text-green-800 transition-colors"
                            onClick={() => handleUpdateVerificationStatus(vendor.id, 'approved')}
                            title="Click to approve vendor"
                          >
                            {vendor.verification_status}
                          </Badge>
                        ) : (
                          <Badge 
                            variant={
                              vendor.verification_status === 'approved' ? 'default' : 'destructive'
                            }
                          >
                            {vendor.verification_status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={vendor.is_active}
                          onCheckedChange={(checked) => handleToggleActive(vendor.id, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(vendor.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {editingVendor === vendor.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateVendor(vendor.id)}
                              disabled={isUpdating || (!editCommissionRate && !editBusinessName.trim())}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelEdit}
                              disabled={isUpdating}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditVendor(vendor)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded details row when editing */}
                    {editingVendor === vendor.id && (
                      <TableRow>
                        <TableCell colSpan={10} className="bg-muted/30 p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Vendor Information */}
                            <Card>
                              <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-sm">
                                  <User className="h-4 w-4" />
                                  Vendor Information
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <Label className="text-xs font-medium">Name</Label>
                                    <p className="text-sm">{vendor.name || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <Label className="text-xs font-medium">Email</Label>
                                    <p className="text-sm">{vendor.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <Label className="text-xs font-medium">Joined Date</Label>
                                    <p className="text-sm">
                                      {new Date(vendor.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                      })}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Performance Metrics */}
                            <Card>
                              <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-sm">
                                  <TrendingUp className="h-4 w-4" />
                                  Performance Metrics
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="text-center">
                                    <Package className="h-5 w-5 text-primary mx-auto mb-1" />
                                    <div className="text-lg font-bold">{vendor.total_orders}</div>
                                    <div className="text-xs text-muted-foreground">Orders</div>
                                  </div>
                                  <div className="text-center">
                                    <DollarSign className="h-5 w-5 text-green-500 mx-auto mb-1" />
                                    <div className="text-lg font-bold">${vendor.total_revenue.toFixed(2)}</div>
                                    <div className="text-xs text-muted-foreground">Revenue</div>
                                  </div>
                                  <div className="text-center">
                                    <TrendingUp className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                                    <div className="text-lg font-bold">
                                      {vendor.total_orders > 0 ? `$${(vendor.total_revenue / vendor.total_orders).toFixed(2)}` : '$0.00'}
                                    </div>
                                    <div className="text-xs text-muted-foreground">Avg Value</div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Reviews */}
                            <Card>
                              <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-sm">
                                  ⭐ Reviews ({vendorReviews.length})
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                {loadingReviews ? (
                                  <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                                    <p className="text-xs text-muted-foreground">Loading reviews...</p>
                                  </div>
                                ) : vendorReviews.length === 0 ? (
                                  <div className="text-center py-4">
                                    <p className="text-sm text-muted-foreground">No reviews yet</p>
                                  </div>
                                ) : (
                                  <div className="space-y-3 max-h-48 overflow-y-auto">
                                    {vendorReviews.map((review) => (
                                      <div key={review.id} className="border-b border-border/50 pb-2 last:border-b-0">
                                        <div className="flex items-start justify-between mb-1">
                                          <p className="text-xs font-medium">{review.reviewer_name}</p>
                                          <div className="flex items-center">
                                            <span className="text-yellow-500 text-xs">
                                              {'★'.repeat(review.rating)}
                                              {'☆'.repeat(5 - review.rating)}
                                            </span>
                                          </div>
                                        </div>
                                        {review.review_text && (
                                          <p className="text-xs text-muted-foreground line-clamp-2">
                                            {review.review_text}
                                          </p>
                                        )}
                                        <p className="text-xs text-muted-foreground/70 mt-1">
                                          {new Date(review.created_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default GraphicsVendorsTab;